/* global google */
import React, { useState, useEffect, useCallback } from 'react'
import { useActions, useValues } from 'kea'
import PropTypes from 'prop-types'

import I18n from 'lib/i18n'
import SlugAutocomplete from 'lib/tags/slug-autocomplete'
import centerZoomFromLocations from 'lib/utils/geo/center-zoom-from-locations'
import scrollToView from 'lib/utils/scroll-to-view'
import getDeviceType from 'lib/utils/get-device-type'
import autoComplete from 'lib/utils/promise-autocomplete'
import { useMapsApiLoader } from 'lib/load/use-maps-api-loader'

import searchLogic from '~/scenes/search/logic'
import filtersLogic from '~/scenes/search/filters-page/logic'

const MAX_ZOOM_FOR_SEARCH = 13
const libraries = ['geometry', 'places']

const { name: deviceName } = getDeviceType() && getDeviceType().os
const isAndroid = deviceName !== undefined && deviceName === 'Android'

const LocationAutocomplete = ({
  unsavedFilter,
  updateUnsavedFilter,
  performedGeolocate,
  fullScreenFiltersPage,
  locationFilterComponent
}) => {
  const [inputValue, setInputValue] = useState('')

  const [optionsParent, setOptionsParent] = useState([])
  const [lastQuery, setLastQuery] = useState('')
  const [isMounted, setIsMounted] = useState(false)

  const { setSuggestionResults } = useActions(filtersLogic)
  const { suggestionsResults } = useValues(filtersLogic)

  const { receiveResults } = useActions(searchLogic)
  const { results, filter } = useValues(searchLogic)

  const { isLoaded } = useMapsApiLoader({
    libraries
  })

  useEffect(() => {
    if (!isMounted) {
      if (!unsavedFilter.location) {
        updateUnsavedFilter({
          ...unsavedFilter,
          location: ''
        })
      }
      setIsMounted(true)
    }
  }, [isMounted, unsavedFilter, updateUnsavedFilter])

  useEffect(() => {
    if (isMounted && unsavedFilter.location_types === 'webcam') {
      setInputValue('')
    }
  }, [isMounted, unsavedFilter.location_types])

  useEffect(() => {
    if (isMounted && (performedGeolocate || unsavedFilter.location !== '')) {
      setInputValue(unsavedFilter.location)
    }
  }, [isMounted, performedGeolocate, unsavedFilter.location, unsavedFilter.location_slug])

  const updateLocation = useCallback(
    value => {
      const changes = {
        ...unsavedFilter,
        location: value.query,
        location_slug: null,
        location_types: unsavedFilter.location_types === 'webcam' ? '' : unsavedFilter.location_types,
        bounds: null,
        center: value.center || unsavedFilter.center,
        zoom: value.zoom || unsavedFilter.zoom,
        width: value.width || unsavedFilter.width,
        height: value.height || unsavedFilter.height,
        coords_from_location: !!value.coords_from_location,
        zoom_out_if_needed: !!value.zoom_out_if_needed,
        fromLocationAutocomplete: true
      }
      updateUnsavedFilter(changes)
    },
    [unsavedFilter, updateUnsavedFilter]
  )

  const fetchOptionsParent = useCallback(
    async (query, callback) => {
      if (query) {
        if (isLoaded) {
          const _results = await autoComplete(query)
          callback(_results)
        }
      } else {
        // eslint-disable-next-line node/no-callback-literal
        callback([])
      }
    },
    [isLoaded]
  )

  const performSearchParent = useCallback(
    (query, data) => {
      if (!data) {
        if (optionsParent && optionsParent.length > 0) {
          data = optionsParent[0]
          query = data.name
          setInputValue(query)
        } else {
          return
        }
      }

      const geocoder = new google.maps.Geocoder()

      geocoder.geocode({ placeId: data.value }, (results, status) => {
        if (status === window.google.maps.GeocoderStatus.OK && results && results[0]) {
          const place = results[0]

          const mapDiv = window.document.getElementById('search-page-main-pigeon-map')
          const width = mapDiv ? mapDiv.clientWidth : unsavedFilter.width
          const height = mapDiv ? mapDiv.clientHeight : unsavedFilter.height

          if (place.geometry.viewport) {
            const viewport = place.geometry.viewport

            const ne = viewport.getNorthEast()
            const sw = viewport.getSouthWest()

            const nw = { lat: ne.lat(), lng: sw.lng() }
            const se = { lat: sw.lat(), lng: ne.lng() }
            const centerZoom = centerZoomFromLocations(
              [
                { latitude: nw.lat, longitude: nw.lng },
                { latitude: se.lat, longitude: se.lng },
                { latitude: place.geometry.location.lat(), longitude: place.geometry.location.lng() }
              ],
              width,
              height,
              0
            )

            updateLocation({
              query,
              center: [centerZoom.center[0], centerZoom.center[1]],
              zoom: Math.min(centerZoom.zoom, MAX_ZOOM_FOR_SEARCH),
              width,
              height,
              zoom_out_if_needed: true
            })
          } else {
            const center = place.geometry.location

            updateLocation({
              query,
              center: [center.lat(), center.lng()],
              zoom: MAX_ZOOM_FOR_SEARCH,
              width,
              height,
              zoom_out_if_needed: true
            })
          }
        }
      })
    },
    [optionsParent, unsavedFilter.height, unsavedFilter.width, updateLocation]
  )

  const getOptionsParent = useCallback(
    query => {
      setLastQuery(query)
      fetchOptionsParent(query, results => {
        if (query !== lastQuery) {
          if (locationFilterComponent && fullScreenFiltersPage) {
            setSuggestionResults(results)
          } else {
            setOptionsParent(results)
          }
        }
      })
    },
    [fetchOptionsParent, fullScreenFiltersPage, lastQuery, locationFilterComponent, setSuggestionResults]
  )

  const handleFocusParent = useCallback(() => {
    if (isAndroid && suggestionsResults.length === 0) {
      const {
        helmet,
        helmet: { meta }
      } = results
      const metaArray = meta.filter(metaTag => metaTag.name !== 'viewport')

      receiveResults(
        {
          ...results,
          helmet: {
            ...helmet,
            meta: [
              ...metaArray,
              { name: 'viewport', content: 'width=device-width, initial-scale=1.0, maximum-scale=1.0,user-scalable=0' }
            ]
          }
        },
        filter
      )
      const filterInputTitleLocation = window.document.querySelector('.location.filter-title')
      scrollToView({ element: filterInputTitleLocation, adjustment: 9999, force: true })
    }
  }, [filter, receiveResults, results, suggestionsResults.length])

  const className = useCallback(() => {
    return `filter-location-autocomplete ${
      locationFilterComponent && fullScreenFiltersPage ? 'NoshowIcon border-radius-8' : ''
    }`
  }, [fullScreenFiltersPage, locationFilterComponent])

  const placeholder = useCallback(() => {
    return locationFilterComponent && fullScreenFiltersPage ? I18n.t('search.filters.choose_your_location') : ''
  }, [fullScreenFiltersPage, locationFilterComponent])

  const handleChangeParent = useCallback(
    event => {
      const newValue = event.target.value
      setInputValue(newValue)

      if (!newValue || newValue.trim() === '') {
        updateUnsavedFilter({
          ...unsavedFilter,
          location: '',
          location_slug: null
        })
      }
    },
    [unsavedFilter, updateUnsavedFilter]
  )

  return (
    <SlugAutocomplete
      inputId={'location-search'}
      inputValueParent={inputValue}
      handleChangeParent={handleChangeParent}
      className={className()}
      placeholder={placeholder()}
      optionsParent={optionsParent}
      handleFocusParent={handleFocusParent}
      fetchOptionsParent={fetchOptionsParent}
      performSearchParent={performSearchParent}
      getOptionsParent={getOptionsParent}
      isLocationFilterAutoComplete
    />
  )
}

LocationAutocomplete.propTypes = {
  updateUnsavedFilter: PropTypes.func.isRequired,
  unsavedFilter: PropTypes.object,
  performedGeolocate: PropTypes.bool,
  fullScreenFiltersPage: PropTypes.bool,
  locationFilterComponent: PropTypes.bool
}

export default LocationAutocomplete
